import { Haptics, ImpactStyle } from '@capacitor/haptics';

import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { motion } from 'framer-motion'

import {
  PiHouse, PiHouseFill,
  PiMoney, PiMoneyFill,
  PiReceipt, PiReceiptFill,
  PiCalculator, PiCalculatorFill
} from 'react-icons/pi'

import { useDashboardStore } from '@/store/useDashboardStore'

interface NavItem {
  path: string
  onClick?: () => void
  label: string
  icon: React.ReactNode
  activeIcon?: React.ReactNode
  className: string
}

const navigationVariants = {
  open: {
    y: 0,
    opacity: 1,
    transition: {
      y: { stiffness: 1000, velocity: -100 }
    }
  },
  closed: {
    y: 100,
    opacity: 0,
    transition: {
      y: { stiffness: 1000 }
    }
  }
}

const Navigation = () => {
  const { t } = useTranslation()
  const location = useLocation()

  const navigationOpen = useDashboardStore((state) => state.navigationOpen)

  const navItems: NavItem[] = [
    {
      path: '/',
      onClick: () => Haptics.impact({ style: ImpactStyle.Light }),
      label: t('navigation.overview'),
      icon: <PiHouse className="w-6 h-6 mt-2" />,
      activeIcon: <PiHouseFill className="w-6 h-6" />,
      className: "gap-3"
    },
    {
      path: '/income',
      onClick: () => Haptics.impact({ style: ImpactStyle.Light }),
      label: t('navigation.income'),
      icon: <PiMoney className="w-6 h-6 mt-2" />,
      activeIcon: <PiMoneyFill className="w-6 h-6" />,
      className: "gap-3"
    },
    {
      path: '/expenses',
      onClick: () => Haptics.impact({ style: ImpactStyle.Light }),
      label: t('navigation.expenses'),
      icon: <PiReceipt className="w-6 h-6 mt-2" />,
      activeIcon: <PiReceiptFill className="w-6 h-6" />,
      className: "gap-3"
    },
    {
      path: '/taxes',
      onClick: () => Haptics.impact({ style: ImpactStyle.Light }),
      label: t('navigation.taxes'),
      icon: <PiCalculator className="w-6 h-6 mt-2" />,
      activeIcon: <PiCalculatorFill className="w-6 h-6" />,
      className: "gap-3"
    }
  ]


  return (
    <motion.nav
      variants={navigationVariants}
      animate={navigationOpen ? 'open' : 'closed'}
      className="bg-white fixed bottom-0 left-0 right-0 border-t border-zinc-200 pb-[var(--safe-area-inset-bottom)]">
      <div className="tour-nav flex justify-between items-end px-4 pt-2 pb-4 max-w-[24rem] mx-auto">
        {navItems.map((item) => {
          const { path, className, icon, activeIcon, label, onClick } = item
          const isActive = location.pathname === path

          return <motion.button
            key={label}
            whileTap={{ scale: 0.95 }}>
            <Link
              onClick={onClick}
              to={path}
              className={`w-[4.5rem] flex flex-col items-center rounded-lg
                ${isActive
                  ? 'text-black-900 font-medium'
                  : 'text-zinc-700 font-medium'
                }
                ${className}
              `}>
              <div className={isActive ? 'text-zinc-900' : 'text-zinc-500'}>
                {isActive ? activeIcon : icon}
              </div>
              <span className="text-xs">
                {label}
              </span>
            </Link>
          </motion.button>
        })}
      </div>
    </motion.nav>
  )
}

export default Navigation
